import {Box, Button, Tooltip} from '@mui/material';
import {getGridStringOperators} from '@mui/x-data-grid-premium';
import {bbox} from '@turf/bbox';
import {bool, func, object} from 'prop-types';
import React, {useCallback, useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import MoreActionsMenu from './FeatureActions/MoreActionsMenu.jsx';
import ZoomToFeatureButton from './FeatureActions/ZoomToFeatureButton.jsx';
import {notify} from '@actions/notifications';
import {useGetCollectionPropertyNames} from '@api/collections.js';
import {fitExtent} from '@app/actions/map';
import {trackEvent} from '@app/analytics';
import {useGetFeatures} from '@app/api/features';
import AreaIndicator from '@app/components/common/AreaIndicator';
import {getHashFromId} from '@app/util/granero.js';
import DataGrid from '@components/common/DataGrid.jsx';
import ReservationsChip from '@components/common/ReservationsChip';

const ReserveQuotaButton = ({collection, hasProducts, onReserveQuotaClick}) => {
  return (
    <Tooltip
      title={!hasProducts ? 'There are no products available to reserve' : ''}
    >
      <span>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          onClick={onReserveQuotaClick}
          sx={{flexShrink: 0}}
          disabled={!hasProducts || collection?.feature_count === 0}
        >
          Reserve Quota
        </Button>
      </span>
    </Tooltip>
  );
};

ReserveQuotaButton.propTypes = {
  collection: object,
  onReserveQuotaClick: func,
  hasProducts: bool,
};

const FeatureTable = ({collection, hasProducts, onReserveQuotaClick}) => {
  const dispatch = useDispatch();

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });
  const [rowCountState, setRowCountState] = useState(0);
  const [queryOptions, setQueryOptions] = useState({});
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

  const titleProperty = collection?.title_property;

  const {data, isLoading} = useGetFeatures(
    {
      collectionId: collection.id,
      params: {
        limit: paginationModel.pageSize,
        offset: paginationModel.pageSize * paginationModel.page,
        ...queryOptions,
      },
    },
    {
      enabled: !!collection,
      onError: () => {
        dispatch(
          notify({
            message: 'There was an error while loading features',
            severity: 'error',
          })
        );
      },
    }
  );

  const {data: properties} = useGetCollectionPropertyNames(
    {collectionId: collection?.id},
    {
      enabled: !!collection,
      select: data => data?.properties ?? [],
    }
  );

  useEffect(() => {
    setRowCountState(prevRowCountState =>
      isLoading ? prevRowCountState : data?.numberMatched
    );
  }, [setRowCountState, data?.numberMatched, isLoading]);

  useEffect(() => {
    // hide property based columns by default
    const newVisibilityModel = properties?.reduce((model, propKey) => {
      model[propKey] = false;
      return model;
    }, {});

    setColumnVisibilityModel(newVisibilityModel);
  }, [properties]);

  const zoomToFeature = useCallback(
    feature => {
      trackEvent('Features Table', {
        action: 'Zoomed to feature',
        featureId: feature.id,
      });
      dispatch(fitExtent(bbox(feature)));
    },
    [dispatch]
  );

  const onFilterChange = useCallback(
    filterModel => {
      const filters = {};

      filterModel.items.forEach(item => {
        const key =
          item.field === 'pl_collection_title'
            ? (titleProperty ?? 'title')
            : item.field;

        const value =
          item.field === 'hashid'
            ? (getHashFromId(item.value) ?? '')
            : item.value;

        if (value) {
          filters[key] = item.operator === 'contains' ? `~${value}` : value;
        }
      });
      // Here you save the data you need from the filter model
      setQueryOptions(filters);
    },
    [titleProperty]
  );

  const columns = [
    {
      field: 'hasReservation',
      headerName: '',
      width: 36,
      disableColumnMenu: true,
      resizable: false,
      filterable: false,
      hideable: false,
      align: 'right',
      renderCell: ({row}) => {
        const productAccessIds = (row.relations ?? [])
          .filter(
            relation => relation.system === 'admin' && relation.key === 'pa'
          )
          .map(relation => relation.value);

        return productAccessIds.length > 0 ? (
          <ReservationsChip
            reservationsProductIds={productAccessIds}
            showLabel={false}
          />
        ) : null;
      },
      cellClassName: 'small-padding--cell',
    },
    {
      field: 'pl_collection_title', // made up field to deconflict from dynamic props
      headerName: `Title${titleProperty && titleProperty !== 'title' ? ` (${titleProperty})` : ''}`,
      minWidth: 200,
      flex: 1,
      hideable: false,
      valueGetter: (value, row) =>
        row.properties[titleProperty] ?? row.properties.title,
      renderCell: ({value}) => {
        if (value) {
          return <div>{value}</div>;
        } else {
          return (
            <Box
              sx={{
                color: 'text.secondary',
                fontStyle: 'italic',
              }}
            >
              Untitled feature
            </Box>
          );
        }
      },
      filterOperators: getGridStringOperators().filter(operator =>
        ['contains', 'equals'].includes(operator.value)
      ),
    },
    {
      field: 'pl_area', // made up field to deconflict from dynamic props
      headerName: 'Area',
      flex: 1,
      filterable: false,
      valueGetter: (value, row) => row.properties['pl:area'],
      renderCell: ({value}) => <AreaIndicator squareMeters={value} />,
    },
    {
      field: 'hashid',
      headerName: 'Reference',
      flex: 1,
      valueGetter: (value, row) => row.properties['pl:ref'],
      filterOperators: getGridStringOperators().filter(operator =>
        ['equals'].includes(operator.value)
      ),
    },
  ];

  //add new columns based on unique properties
  properties?.map(prop =>
    columns.push({
      field: prop,
      headerName: prop,
      flex: 1,
      sortable: false,
      valueGetter: (value, row) => row.properties[prop],
      filterOperators: getGridStringOperators().filter(operator =>
        ['contains', 'equals'].includes(operator.value)
      ),
    })
  );

  columns.push({
    field: 'actions',
    headerName: 'Actions',
    type: 'actions',
    getActions: ({row}) => [
      <ZoomToFeatureButton
        key={`${row.id}-zoom-to`}
        onClick={() => zoomToFeature(row)}
      />,
      <MoreActionsMenu
        key={`${row.id}-more-actions`}
        feature={row}
        isOwner={collection?.permissions?.is_owner}
      />,
    ],
  });

  return (
    <DataGrid
      disableRowSelectionOnClick
      rows={data?.features || []}
      columns={columns}
      loading={isLoading}
      paginationMode="server"
      pagination
      rowCount={rowCountState}
      pageSizeOptions={[50]}
      paginationModel={paginationModel}
      onPaginationModelChange={model => setPaginationModel({...model})}
      columnHeaderHeight={40}
      filterMode="server"
      onFilterModelChange={onFilterChange}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={newModel =>
        setColumnVisibilityModel(newModel)
      }
      showToolbar
      slotProps={{
        toolbar: {
          children: (
            <ReserveQuotaButton
              collection={collection}
              hasProducts={hasProducts}
              onReserveQuotaClick={onReserveQuotaClick}
            />
          ),
        },
      }}
    />
  );
};

FeatureTable.propTypes = {
  collection: object,
  onReserveQuotaClick: func,
  hasProducts: bool,
};

export default FeatureTable;
